import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";

/**
 * CATÁLOGO DE PRODUTOS.
 *
 * O que faz a Greenn, a App Store e a Play Store conviverem: o MESMO plano premium
 * existe uma vez por origem, cada uma com o id que aquela loja usa, e as três liberando
 * o mesmo acesso.
 *
 * O "Libera" NÃO é campo livre: a API devolve as tags que o CONTEÚDO realmente exige, e
 * o admin escolhe delas. Sem isso, dá pra criar um produto que libera uma tag que nenhum
 * conteúdo usa — e o cliente paga por acesso a nada.
 */

const NOME_ORIGEM = {
  greenn: "Greenn",
  app_store: "App Store",
  play_store: "Play Store",
  internal: "Interno",
};

export default function Catalog() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [novo, setNovo] = useState(null); // { source }

  const carregar = () =>
    rodar(async () => setDados(await apiGet(API_ROUTES.ADMIN.products())));

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = useMemo(
    () => (dados?.catalogo || []).reduce((s, g) => s + g.produtos.length, 0),
    [dados]
  );
  const comProduto = useMemo(
    () => (dados?.catalogo || []).filter((g) => g.produtos.length).length,
    [dados]
  );

  const alternarAtivo = (p) =>
    rodar(async () => {
      await apiSend(API_ROUTES.ADMIN.product(p.id), "PATCH", { active: !p.active });
      await carregar();
    }, p.active ? "Produto desativado." : "Produto ativado.");

  const criar = (input) =>
    rodar(async () => {
      await apiSend(API_ROUTES.ADMIN.products(), "POST", input);
      setNovo(null);
      await carregar();
    }, "Produto cadastrado.");

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Catálogo"
        subtitulo="O produto que a loja cobra e o acesso que ele libera. O mesmo plano existe uma vez por origem."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 13, color: "var(--text2)" }}>
            {total} produtos · {comProduto} {comProduto === 1 ? "origem" : "origens"}
          </span>
          <button className="adm__btn" onClick={() => setNovo({ source: "app_store" })}>
            + Novo produto
          </button>
        </div>

        {!dados && <p className="adm__vazio">Carregando…</p>}

        {dados?.catalogo.map((grupo) => (
          <section key={grupo.source} style={{ marginBottom: 26 }}>
            <h2
              style={{
                fontSize: 14,
                margin: "0 0 10px",
                display: "flex",
                alignItems: "center",
                gap: 9,
              }}
            >
              {NOME_ORIGEM[grupo.source] || grupo.source}
              <span className="adm__chip">{grupo.produtos.length}</span>
            </h2>

            {grupo.produtos.length === 0 ? (
              <div className="adm__card">
                <div className="adm__vazio">
                  <strong style={{ color: "var(--text2)", display: "block", marginBottom: 5 }}>
                    Nenhum produto cadastrado
                  </strong>
                  Cadastre aqui o <code className="adm__code">product_id</code> que você criou
                  na loja.
                </div>
              </div>
            ) : (
              <div className="adm__card" style={{ overflow: "hidden" }}>
                <table className="adm__tabela">
                  <thead>
                    <tr>
                      <th>ID do produto</th>
                      <th>Nome</th>
                      <th>Libera</th>
                      <th>Periodicidade</th>
                      <th style={{ textAlign: "right" }}>Ativo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.produtos.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <code className="adm__code">{p.productId}</code>
                        </td>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {p.entitlements.map((e) => (
                              <span key={e} className="adm__chip adm__chip--blue">
                                {e}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ color: "var(--text2)" }}>
                          {dados.intervalos.find((i) => i.key === p.interval)?.label || "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {/* Desativar um produto com assinante ativo é RECUSADO pelo
                              backend — revogaria o acesso de quem pagou. */}
                          <button
                            onClick={() => alternarAtivo(p)}
                            title={
                              p.assinantes
                                ? `${p.assinantes.toLocaleString("pt-BR")} assinante(s)`
                                : "Sem assinantes"
                            }
                            style={{
                              width: 38,
                              height: 21,
                              borderRadius: 999,
                              border: "none",
                              cursor: "pointer",
                              background: p.active ? "var(--green)" : "var(--border)",
                              position: "relative",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: 2,
                                left: p.active ? 19 : 2,
                                width: 17,
                                height: 17,
                                borderRadius: "50%",
                                background: "#fff",
                                transition: "left .15s",
                              }}
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}
      </div>

      {novo && dados && (
        <ModalProduto
          dados={dados}
          origemInicial={novo.source}
          onFechar={() => setNovo(null)}
          onSalvar={criar}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

function ModalProduto({ dados, origemInicial, onFechar, onSalvar }) {
  const [source, setSource] = useState(origemInicial);
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [interval, setInterval] = useState("monthly");
  const [entitlements, setEntitlements] = useState([]);

  const alternarTag = (t) =>
    setEntitlements((e) => (e.includes(t) ? e.filter((x) => x !== t) : [...e, t]));

  const valido = source && productId.trim() && name.trim() && entitlements.length;

  return (
    <div className="adm__modalFundo" onClick={onFechar}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()}>
        <h3>Novo produto</h3>
        <p className="adm__modalHint">
          O <strong>id do produto</strong> precisa ser <strong>idêntico</strong> ao que você
          criou na loja — é por ele que a compra é reconhecida.
        </p>

        <div className="adm__campo">
          <label>Origem</label>
          <select
            className="adm__select"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            {dados.origens.map((o) => (
              <option key={o} value={o}>
                {NOME_ORIGEM[o] || o}
              </option>
            ))}
          </select>
        </div>

        <div className="adm__campo">
          <label>ID do produto</label>
          <input
            className="adm__input"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            placeholder="ex.: reinoplay_premium_monthly"
            style={{ fontFamily: '"Roboto Mono", monospace' }}
          />
        </div>

        <div className="adm__campo">
          <label>Nome</label>
          <input
            className="adm__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex.: Premium Mensal (iOS)"
          />
        </div>

        <div className="adm__campo">
          <label>Libera — escolha os acessos</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
            {dados.tagsDisponiveis.map((t) => {
              const on = entitlements.includes(t.tag);
              return (
                <button
                  key={t.tag}
                  onClick={() => alternarTag(t.tag)}
                  className={`adm__chip ${on ? "adm__chip--red" : ""}`}
                  style={{ cursor: "pointer", border: on ? "1px solid var(--red)" : undefined }}
                  title={`${t.conteudos} conteúdo(s) exigem esta tag`}
                >
                  {t.tag}
                  <span style={{ opacity: 0.6, fontSize: 10 }}>{t.conteudos}</span>
                </button>
              );
            })}
          </div>
          {!entitlements.length && (
            <p style={{ fontSize: 11.5, color: "var(--orange)", margin: "8px 0 0" }}>
              Um produto que não libera nada faz o cliente pagar e não desbloquear nada.
            </p>
          )}
        </div>

        <div className="adm__campo">
          <label>Periodicidade</label>
          <select
            className="adm__select"
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
          >
            {dados.intervalos.map((i) => (
              <option key={i.key} value={i.key}>
                {i.label}
              </option>
            ))}
          </select>
        </div>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>
            Cancelar
          </button>
          <button
            className="adm__btn"
            disabled={!valido}
            onClick={() => onSalvar({ source, productId, name, entitlements, interval })}
          >
            Cadastrar produto
          </button>
        </div>
      </div>
    </div>
  );
}
